/**
 * 営業フォーム4種のテンプレート (雛形) 定義。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §4.2 / §4.3 / §4.4
 *
 * FormsListPage の営業タブから「+新規」ドロップダウンで選択された種別に応じて、
 * 該当テンプレを Form の初期 elements + メタフィールド (tag/dealStageHint/category) として
 * Firestore に書込み、FormBuilderPage に遷移する。
 *
 * 動的フィールド (briefing_schedule_picker / product_picker / assignee_picker) を含む。
 * これらは FormBuilderPage 上でラベル編集可能。public form では runtime に候補が取得される。
 *
 * 2026-08-24 追加: 顧客情報確認・更新 (customer_info)。
 *   dealStageHint=null / category='customer_info' で作成し、送信時に Deal を作らない
 *   唯一の営業フォーム種別。回答は顧客マスタへ上書き反映される
 *   (contactOverwritePolicy='overwrite' で作成)。
 */
import type {
  ContactOverwritePolicy,
  FormCategory,
  FormElement,
  DealStageHint,
} from '@goenhub/shared';

export type SalesFormTemplateKey =
  | 'document_request'
  | 'briefing'
  | 'quote'
  | 'customer_info';

export interface SalesFormTemplate {
  key: SalesFormTemplateKey;
  /** UI 表示用ラベル (種別選択ドロップダウン) */
  label: string;
  /** 1行の説明 */
  description: string;
  /** Form.name の初期値 */
  formName: string;
  /** Form.title の初期値 */
  formTitle: string;
  /** Form.description の初期値 */
  formDescription: string;
  /** Form.dealStageHint。null = 送信時に Deal を作らない (customer_info)。 */
  dealStageHint: DealStageHint | null;
  /** Form.category (dealStageHint と1:1、customer_info のみ hint なし) */
  category: FormCategory;
  /** Form.contactOverwritePolicy の初期値 (未指定は 'fill' のまま) */
  contactOverwritePolicy?: ContactOverwritePolicy;
  /** 雛形 elements */
  elements: FormElement[];
}

let elementIdCounter = 0;
function nextId(prefix: string): string {
  elementIdCounter++;
  return `${prefix}_${elementIdCounter}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 共通の連絡先 4 フィールド (氏名 / メール / 電話 / 職業) */
function commonContactFields(): FormElement[] {
  return [
    {
      id: nextId('name'),
      kind: 'field',
      fieldType: 'text',
      label: '氏名',
      required: true,
      placeholder: '例: 山田 太郎',
    },
    {
      id: nextId('email'),
      kind: 'field',
      fieldType: 'email',
      label: 'メールアドレス',
      required: true,
      placeholder: 'example@example.com',
    },
    {
      id: nextId('tel'),
      kind: 'field',
      fieldType: 'tel',
      label: '電話番号',
      required: true,
      placeholder: '例: 090-0000-0000',
    },
    {
      id: nextId('job'),
      kind: 'field',
      fieldType: 'text',
      label: '職業',
      required: true,
      placeholder: '例: 美容サロン経営',
    },
  ];
}

const documentRequestTemplate: SalesFormTemplate = {
  key: 'document_request',
  label: '資料請求',
  description: '資料公開URL + パスワードを自動返信。terminal な Deal を作成。',
  formName: '資料請求フォーム',
  formTitle: '資料請求',
  formDescription:
    'ご記入いただいたメールアドレス宛に、資料の閲覧URLと閲覧パスワードをお送りいたします。',
  dealStageHint: 'document_requested',
  category: 'document_download',
  elements: [
    ...commonContactFields(),
    {
      id: nextId('inquiry'),
      kind: 'field',
      fieldType: 'textarea',
      label: '導入にあたって知りたいこと',
      required: false,
      placeholder: '機能・価格・導入事例など、知りたいことをご記入ください',
    },
  ],
};

const briefingTemplate: SalesFormTemplate = {
  key: 'briefing',
  label: '個別説明申込',
  description: '候補日 (briefing_schedules) を動的取得。promotionLevel が customer に昇格。',
  formName: '個別説明申込フォーム',
  formTitle: '個別説明会のお申込み',
  formDescription:
    'オンラインまたは現地での個別説明会のお申込みフォームです。候補日からご希望の日時を選択してください。',
  dealStageHint: 'briefing',
  category: 'explanation',
  elements: [
    ...commonContactFields(),
    {
      id: nextId('style'),
      kind: 'field',
      fieldType: 'radio',
      label: '参加スタイル',
      required: true,
      options: ['オンライン', '現地'],
    },
    {
      id: nextId('inquiry'),
      kind: 'field',
      fieldType: 'textarea',
      label: '導入にあたって知りたいこと',
      required: false,
      placeholder: '事前に知りたいことをご記入ください',
    },
    {
      id: nextId('schedule'),
      kind: 'field',
      fieldType: 'briefing_schedule_picker',
      label: '候補日 (複数選択可)',
      required: true,
      helpText:
        'ご都合のつく候補日を複数選択してください。後ほど運営から日程確定のご連絡をいたします。',
    },
  ],
};

const quoteTemplate: SalesFormTemplate = {
  key: 'quote',
  label: '見積依頼',
  description: '製品 / 担当者を動的取得。送信時に見積書 PDF + メールが自動送信される (Phase 6)。',
  formName: '見積依頼フォーム',
  formTitle: '見積依頼',
  formDescription:
    'ご希望の製品をお選びいただき、お見積もりをお送りいたします。',
  dealStageHint: 'quote',
  category: 'quote',
  elements: [
    {
      id: nextId('name'),
      kind: 'field',
      fieldType: 'text',
      label: '顧客氏名',
      required: true,
      placeholder: '例: 山田 太郎',
    },
    {
      id: nextId('email'),
      kind: 'field',
      fieldType: 'email',
      label: 'メールアドレス',
      required: true,
      placeholder: 'example@example.com',
    },
    {
      id: nextId('products'),
      kind: 'field',
      fieldType: 'product_picker',
      label: '見積製品 (複数選択可)',
      required: true,
      helpText: '現在販売中の製品から選択できます。',
    },
    {
      id: nextId('payment'),
      kind: 'field',
      fieldType: 'radio',
      label: '支払い方法',
      required: true,
      options: ['振込', 'クレジットカード'],
    },
    {
      id: nextId('installments'),
      kind: 'field',
      fieldType: 'select',
      label: '支払い回数 (クレジットカード時)',
      required: false,
      options: ['12', '24', '36', '48', '60'],
      helpText: 'クレジットカード支払いの場合のみ選択してください。',
    },
    {
      id: nextId('tradein'),
      kind: 'field',
      fieldType: 'radio',
      label: '下取り有無',
      required: true,
      options: ['あり', 'なし'],
    },
    {
      id: nextId('tradeintype'),
      kind: 'field',
      fieldType: 'select',
      label: '下取りタイプ',
      required: false,
      options: ['割引', 'キャッシュバック'],
      helpText: '下取りありの場合のみ選択してください。',
    },
    {
      id: nextId('assignee'),
      kind: 'field',
      fieldType: 'assignee_picker',
      label: '担当者',
      required: true,
      helpText: 'ご希望の担当者をお選びください。',
    },
  ],
};

/**
 * 顧客情報確認・更新テンプレート (2026-08-24)。
 *
 * 背景: 取扱商品がドイツ製のため、購入者の英語氏名・英語住所・電話番号・メアドを
 * メーカーへ報告する必要がある。見積段階で住所未入力のまま進むケースが多く、
 * 顧客に自分の登録情報を確認・更新してもらう専用フォーム。
 *
 * 挙動 (他 3 種別との違い):
 *   - 送信しても Deal は作成されない (resolveDealForSubmission が category='customer_info' で skip)
 *   - 回答は mail_contacts の該当枠へ自動反映。contactOverwritePolicy='overwrite' で
 *     作成されるため、既存値も最新の申告で上書きされる (氏名一致のみの同定は除く)
 *   - 住所 (billing_address) は複数住所カードの仕組みに従う:
 *     既存と同じ住所 → 既存カードを更新して既定に / 別の住所 → カード追加して既定に (旧カードは残る)
 */
const customerInfoTemplate: SalesFormTemplate = {
  key: 'customer_info',
  label: '顧客情報確認・更新',
  description:
    '登録情報 (住所・英語表記等) の確認・更新用。回答は顧客データへ自動反映。Deal は作成されない。',
  formName: '顧客情報確認・更新フォーム',
  formTitle: 'ご登録情報の確認・更新',
  formDescription:
    'お取引にあたり必要なご登録情報の確認をお願いいたします。現在のご登録内容から変更がある項目も含め、最新の情報をご入力ください。ご入力いただいた内容でご登録情報を更新いたします。',
  dealStageHint: null,
  category: 'customer_info',
  contactOverwritePolicy: 'overwrite',
  elements: [
    {
      id: nextId('lastname'),
      kind: 'field',
      fieldType: 'last_name',
      label: '姓',
      required: true,
      placeholder: '例: 山田',
    },
    {
      id: nextId('firstname'),
      kind: 'field',
      fieldType: 'first_name',
      label: '名',
      required: true,
      placeholder: '例: 太郎',
    },
    {
      id: nextId('lastkana'),
      kind: 'field',
      fieldType: 'last_name_kana',
      label: 'せい（フリガナ）',
      required: true,
      placeholder: '例: ヤマダ',
    },
    {
      id: nextId('firstkana'),
      kind: 'field',
      fieldType: 'first_name_kana',
      label: 'めい（フリガナ）',
      required: true,
      placeholder: '例: タロウ',
    },
    {
      id: nextId('email'),
      kind: 'field',
      fieldType: 'email',
      label: 'メールアドレス',
      required: true,
      placeholder: 'example@example.com',
    },
    {
      id: nextId('phone'),
      kind: 'field',
      fieldType: 'phone',
      label: '電話番号',
      required: true,
      placeholder: '例: 090-0000-0000',
    },
    {
      id: nextId('company'),
      kind: 'field',
      fieldType: 'company_name',
      label: '会社名',
      required: false,
      placeholder: '例: 株式会社カウデザイン（法人のお客様のみ）',
    },
    {
      id: nextId('address'),
      kind: 'field',
      fieldType: 'billing_address',
      label: '住所',
      required: true,
      helpText:
        '書類の送付・ご請求に使用する住所です。郵便番号・都道府県からご入力ください。',
    },
    {
      id: nextId('birthdate'),
      kind: 'field',
      fieldType: 'birthdate',
      label: '生年月日（西暦）',
      required: true,
    },
    {
      id: nextId('english'),
      kind: 'field',
      fieldType: 'english_profile',
      label: '英語表記のご登録情報（氏名・住所）',
      required: true,
      helpText:
        '海外メーカーへの登録に使用します。氏名・住所を英語表記（ローマ字）でご入力ください。',
    },
  ],
};

export const SALES_FORM_TEMPLATES: Record<SalesFormTemplateKey, SalesFormTemplate> = {
  document_request: documentRequestTemplate,
  briefing: briefingTemplate,
  quote: quoteTemplate,
  customer_info: customerInfoTemplate,
};

export const SALES_FORM_TEMPLATE_LIST: SalesFormTemplate[] = [
  documentRequestTemplate,
  briefingTemplate,
  quoteTemplate,
  customerInfoTemplate,
];
